import { useRef, useState } from 'react'
import { useEffectOnce, useMountedState } from 'react-use'
import { AxiosError } from 'axios'
import { FunctionReturningPromise, PromiseType } from 'react-use/lib/misc/types'

import { useCallbackOnce } from 'utils/use-callback-once'

interface IResult<T> {
  isLoading: boolean
  request: T
  response?: T extends (...args: infer A) => Promise<infer R> ? R : never
  requestCount: number
  error?: unknown
}

interface Options<T extends FunctionReturningPromise> {
  preventThrow?: boolean
  initialState?: StateFromFunctionReturningPromise<T>
  clearPrevResponse?: boolean
  firstRenderRequest?: boolean
}

export type AsyncState<T> =
  | {
      isLoading: boolean
      error?: undefined
      response?: undefined
    }
  | {
      isLoading: true
      error?: AxiosError | undefined
      response?: T
    }
  | {
      isLoading: false
      error: AxiosError
      response?: undefined
    }
  | {
      isLoading: false
      error?: undefined
      response: T
    }

type StateFromFunctionReturningPromise<T extends FunctionReturningPromise> = AsyncState<
  PromiseType<ReturnType<T>>
>

//TODO: change it to react-query
export const useRequestCallback = <T extends FunctionReturningPromise>(
  fn: T,
  options: Options<T> = {},
): IResult<T> => {
  const lastCallId = useRef(0)
  const isMounted = useMountedState()
  const requestCount = useRef(0)
  const [state, set] = useState<StateFromFunctionReturningPromise<T>>(
    options.initialState || {
      isLoading: false,
      error: undefined,
      response: undefined,
    },
  )

  const request = useCallbackOnce((...args) => {
    const callId = ++lastCallId.current

    set((prevState) => ({
      ...prevState,
      error: undefined,
      response: options.clearPrevResponse ? undefined : prevState.response,
      isLoading: true,
    }))
    requestCount.current++
    return fn(...args).then(
      (data) => {
        isMounted() && callId === lastCallId.current && set({ response: data, isLoading: false })

        return data
      },

      (err) => {
        isMounted() && callId === lastCallId.current && set({ error: err, isLoading: false })

        if (!options.preventThrow) {
          throw err
        }
      },
    )
  })

  useEffectOnce(() => {
    if (options.firstRenderRequest) request()
  })

  return {
    isLoading: state.isLoading,
    response: state.response,
    request: request,
    error: state.error,
    requestCount: requestCount.current,
  } as IResult<T>
}
